import type { IColumnMapping } from "../../gemd-builder";
import { isColumnMapping } from "../../gemd-builder";
import { isOfType } from "../../utils";

import type {
  IAttributeTemplate,
  IDataObjectOrTemplate,
  IObjectTemplate,
  IRunObject,
  IScalarAttribute,
  ISpecObjectWithTemplate,
} from ".";

export interface ILinkByUid {
  type: "link_by_uid";
  id: string;
  scope: string;
}

export const isLinkUid = isOfType<ILinkByUid>("link_by_uid");

export interface IMappableField {
  mapping: IColumnMapping | null;
  value: string | null;
}

export const isMappableField = (arg: any): arg is IMappableField =>
  isColumnMapping(arg?.mapping) && typeof arg?.value === "string";

export interface Uids {
  [scope: string]: string;
  id: string;
}

interface HasUids {
  uids: Uids;
}
export function getDataObjectId<Type extends HasUids | ILinkByUid | undefined>(
  dataObject: Type,
): undefined extends Type
  ? Type extends undefined
    ? undefined
    : string
  : string {
  if (dataObject) {
    // this is a hack for a bug in TS 4.9
    // https://github.com/microsoft/TypeScript/pull/51502
    const anyObject: any = dataObject;
    if ("uids" in anyObject) {
      return anyObject.uids.id as any;
    }
    return anyObject.id;
  }
  return undefined as any;
}

/* 
Gets bounds for an attribute out of a material history context using the following logic:
  If there is an Attribute Template referenced in the Attribute:
    If there is an Object Template referenced in the object:
      Find the Object Template Attribute tuple that has the matching Attribute Template as its first element
      If the second tuple element is not null:
        Use that bounds to validate
      Else:
        Use the Attribute Template bounds
    Else:
      Use the Attribute Template bounds
  Else:
    No validation
*/
export function getBoundsForAttribute(
  attribute: IScalarAttribute,
  object: IRunObject,
  materialContext: Partial<Record<string, IDataObjectOrTemplate>>,
) {
  if (attribute.template) {
    const templateAttributeId = getDataObjectId(attribute.template);
    const attributeTemplate = materialContext[templateAttributeId] as
      | IAttributeTemplate
      | undefined;

    if (attributeTemplate) {
      const objectSpecId = getDataObjectId(object.spec);
      const spec = materialContext[objectSpecId] as
        | ISpecObjectWithTemplate
        | undefined;

      if (spec) {
        const specTemplateId = getDataObjectId(spec.template);

        if (specTemplateId) {
          const objectTemplate = materialContext[specTemplateId] as
            | IObjectTemplate
            | undefined;

          if (objectTemplate) {
            const combinedAttributes = [
              ...(objectTemplate.conditions ?? []),
              ...(objectTemplate.parameters ?? []),
              ...(objectTemplate.properties ?? []),
            ];

            const templateAttributePair = combinedAttributes.find(
              ([templatedAttribute, bounds]) =>
                Boolean(bounds) &&
                getDataObjectId(templatedAttribute) ===
                  attributeTemplate.uids.id,
            );

            if (templateAttributePair) {
              return templateAttributePair[1];
            }
          }
        }
      }

      return attributeTemplate.bounds;
    }
  }
}
